/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import orderBy from 'lodash.orderby';
import React from 'react';
import {
  DataRowRecord,
  DataColumn,
  Selector,
  SortDirection,
  DefaultSortField,
  SortFunction,
} from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getRowProperty<T extends Record<string, any>>(
  row: T,
  selector: Selector<T> | undefined | null | unknown,
  rowIdx: number,
): React.ReactNode {
  if (!selector) {
    return null;
  }

  if (typeof selector !== 'string' && typeof selector !== 'function') {
    throw new Error(
      'selector must be a . delimited string eg (my.property) or function (e.g. row => row.field',
    );
  }

  if (selector && typeof selector === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return selector(row, rowIdx);
  }

  return selector.split('.').reduce((acc, part) => {
    const arr = part.match(/[^\]\\[.]+/g);
    if (arr && arr.length > 1) {
      for (let i = 0; i < arr.length; i++) {
        return acc[arr[i]][arr[i + 1]];
      }
    }

    return acc[part];
  }, row);
}

export const nullFn = (): null => null;

export function isEmpty(field: string | number | undefined = ''): boolean {
  if (typeof field === 'number') {
    return false;
  }

  return !field || field.length === 0;
}

export function uniqueifyDataColumns<T>(
  columns: DataColumn<T>[],
): DataColumn<T>[] {
  return columns.map((col, idx) => {
    const uniqueCol: DataColumn<T> = {
      ...col,
    };

    if (!col.id) {
      uniqueCol.id = idx + 1;

      return uniqueCol;
    }

    return uniqueCol;
  });
}

export function insertItem<T extends DataRowRecord>(
  array: Array<T> = [],
  item: T,
  idx = 0,
): Array<T> {
  return [...array.slice(0, idx), item, ...array.slice(idx)];
}

export function removeItem<T extends DataRowRecord>(
  array: Array<T> = [],
  item: T,
  keyField: string | number = 'id',
): Array<T> {
  const newArray = array.slice();

  if (item[keyField]) {
    newArray.splice(
      newArray.findIndex((a: T) => a[keyField] === item[keyField]),
      1,
    );
  } else {
    newArray.splice(
      newArray.findIndex((a) => a === item),
      1,
    );
  }

  return newArray;
}

export function isRowSelected<T extends DataRowRecord>(
  row: T,
  selectedRows: Array<T> = [],
  keyField: string | number = 'id',
): boolean {
  if (row[keyField]) {
    return selectedRows.some((r) => r[keyField] === row[keyField]);
  }

  return selectedRows.some((r) => r === row);
}

export function getNumberOfPages(
  rowCount: number,
  rowsPerPage: number,
): number {
  return Math.ceil(rowCount / rowsPerPage);
}

export function recalculatePage(prevPage: number, nextPage: number): number {
  return Math.min(prevPage, nextPage);
}

export function getColumnById<T>(
  id: DefaultSortField,
  columns: DataColumn<T>[],
): DataColumn<T> | undefined {
  if (typeof id === undefined) {
    return undefined;
  }

  return columns.find((col) => col.id === id);
}

export function getSortDirection(
  ascDirection: boolean | undefined = false,
): SortDirection {
  return ascDirection ? 'asc' : 'desc';
}

export function sort<T = DataRowRecord>(
  rows: Array<T>,
  selector: Selector<T> | null,
  direction: SortDirection,
  sortFn?: SortFunction<T> | null,
): T[] {
  if (!selector) {
    return rows;
  }

  if (sortFn && typeof sortFn === 'function') {
    return sortFn(rows, selector, direction);
  }

  return orderBy(rows, selector, direction);
}
